import { useState, useEffect, useCallback } from 'react'
import {
  Lightbulb,
  MessageSquare,
  Clock,
  CheckCircle2,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  ShieldAlert,
  ChevronUp,
  Rocket,
  FileCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/lib/contexts/auth-context'
import { PageHeader, FluentCard, StatCard } from '@/components/layout/page-header'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  featureRequestService,
  categoryLabels,
  type FeatureRequestDto,
  type FeatureRequestCategory,
  type FeatureRequestStatus,
  type FeatureStatsDto,
} from '@/lib/api/services/featureRequests'

// Status badge styles
const statusBadgeConfig: Record<FeatureRequestStatus, { label: string; className: string }> = {
  Submitted: {
    label: 'Submitted',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
  UnderReview: {
    label: 'Under Review',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  Planned: {
    label: 'Planned',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  },
  InProgress: {
    label: 'In Progress',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  Completed: {
    label: 'Completed',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  Declined: {
    label: 'Declined',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
}

// Category badge styles
const categoryBadgeConfig: Record<FeatureRequestCategory, string> = {
  UiUx: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  WordAddin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  ExcelAddin: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  PowerPointAddin: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  OutlookAddin: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  Api: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  Integrations: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  AiAutomation: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
  Workflows: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  Templates: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  Other: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
}

export default function FeatureRequestsAdmin() {
  const { user: currentUser, hasPermission, isLoading: authLoading } = useAuth()
  const canAccessPage = currentUser?.isSuperAdmin || hasPermission('system.admin')

  const [requests, setRequests] = useState<FeatureRequestDto[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<FeatureStatsDto | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const { toast } = useToast()

  // Modal states
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequestDto | null>(null)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Status update form
  const [newStatus, setNewStatus] = useState<FeatureRequestStatus>('Submitted')
  const [adminResponse, setAdminResponse] = useState('')

  // DataTable state
  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      sortField: 'createdAt',
      sortDirection: 'desc',
    },
  })

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to manage Feature Requests. Please contact your administrator.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    setLoading(true)
    try {
      const params = getODataParams()
      const response = await featureRequestService.getAdminFeatureRequests({
        ...params,
        count: true,
      })
      setRequests(response.value)
      setTotalCount(response['@odata.count'] ?? response.value.length)
    } catch (error) {
      toast({
        title: 'Error fetching feature requests',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [getODataParams, toast])

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const data = await featureRequestService.getStats()
      setStats(data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    } finally {
      setStatsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Actions
  const handleUpdateStatus = async () => {
    if (!selectedRequest) return
    setActionLoading(true)
    try {
      await featureRequestService.updateStatus(selectedRequest.id, {
        status: newStatus,
        adminResponse: adminResponse || undefined,
      })
      toast({
        title: 'Status updated',
        description: `Feature request status has been updated to ${statusBadgeConfig[newStatus].label}.`,
      })
      setStatusModalOpen(false)
      setAdminResponse('')
      fetchRequests()
      fetchStats()
    } catch (error) {
      toast({
        title: 'Failed to update status',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedRequest) return
    setActionLoading(true)
    try {
      await featureRequestService.deleteFeatureRequest(selectedRequest.id)
      toast({
        title: 'Feature request deleted',
        description: `"${selectedRequest.title}" has been deleted.`,
      })
      setDeleteDialogOpen(false)
      fetchRequests()
      fetchStats()
    } catch (error) {
      toast({
        title: 'Failed to delete feature request',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const openStatusModal = (request: FeatureRequestDto) => {
    setSelectedRequest(request)
    setNewStatus(request.status)
    setAdminResponse(request.adminResponse || '')
    setStatusModalOpen(true)
  }

  // Table columns
  const columns: ColumnDef<FeatureRequestDto>[] = [
    {
      id: 'title',
      header: 'Title',
      accessor: 'title',
      sortable: true,
      filterable: true,
      cell: (row) => (
        <div className="max-w-[300px]">
          <div className="font-medium text-[#1a1a1a] dark:text-white truncate">
            {row.title}
          </div>
          <div className="text-xs text-[#888] truncate mt-0.5">
            {row.description.slice(0, 80)}...
          </div>
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      accessor: 'category',
      sortable: true,
      filterType: 'select',
      filterOptions: Object.entries(categoryLabels).map(([value, label]) => ({
        label,
        value,
      })),
      cell: (row) => (
        <Badge className={categoryBadgeConfig[row.category]}>
          {categoryLabels[row.category]}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      filterType: 'select',
      filterOptions: Object.entries(statusBadgeConfig).map(([value, { label }]) => ({
        label,
        value,
      })),
      cell: (row) => (
        <Badge className={statusBadgeConfig[row.status].className}>
          {statusBadgeConfig[row.status].label}
        </Badge>
      ),
    },
    {
      id: 'submittedByName',
      header: 'Submitted By',
      accessor: (row) => row.submittedByName || row.submittedByEmail || 'Anonymous',
      sortable: true,
    },
    {
      id: 'voteCount',
      header: 'Votes',
      accessor: 'voteCount',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          <ChevronUp className="h-4 w-4 text-violet-500" />
          <span className="font-semibold text-violet-600 dark:text-violet-400">
            {row.voteCount}
          </span>
        </div>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: () => null,
      cell: (row) => (
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRequest(row)
                    setViewModalOpen(true)
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>View full details</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    openStatusModal(row)
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Update status</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRequest(row)
                    setDeleteDialogOpen(true)
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Delete permanently</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feature Requests"
        description="Manage user feature requests and update their status"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Admin', href: '/dashboard/admin' },
          { label: 'Feature Requests' },
        ]}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard
          label="Total Requests"
          value={stats?.totalRequests ?? 0}
          icon={Lightbulb}
          color="from-violet-400 to-purple-600"
          loading={statsLoading}
        />
        <StatCard
          label="Submitted"
          value={stats?.submittedCount ?? 0}
          icon={MessageSquare}
          color="from-slate-400 to-slate-600"
          loading={statsLoading}
        />
        <StatCard
          label="Under Review"
          value={stats?.underReviewCount ?? 0}
          icon={Clock}
          color="from-blue-400 to-blue-600"
          loading={statsLoading}
        />
        <StatCard
          label="Planned"
          value={stats?.plannedCount ?? 0}
          icon={FileCheck}
          color="from-purple-400 to-purple-600"
          loading={statsLoading}
        />
        <StatCard
          label="In Progress"
          value={stats?.inProgressCount ?? 0}
          icon={Rocket}
          color="from-amber-400 to-amber-600"
          loading={statsLoading}
        />
        <StatCard
          label="Completed"
          value={stats?.completedCount ?? 0}
          icon={CheckCircle2}
          color="from-emerald-400 to-emerald-600"
          loading={statsLoading}
        />
      </div>

      {/* Top Voted Section */}
      {stats?.topVoted && stats.topVoted.length > 0 && (
        <FluentCard>
          <h3 className="text-lg font-semibold text-[#1a1a1a] dark:text-white mb-4 flex items-center gap-2">
            <ChevronUp className="h-5 w-5 text-violet-500" />
            Top Voted Requests
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.topVoted.slice(0, 6).map((item, index) => (
              <div
                key={item.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-[#f5f5f5] dark:bg-[#1f1f1f] border border-[#e5e5e5] dark:border-[#3d3d3d]"
              >
                <div className="flex flex-col items-center min-w-[40px]">
                  <span className="text-xs text-[#888] font-medium">#{index + 1}</span>
                  <div className="flex items-center gap-0.5 text-violet-600 dark:text-violet-400">
                    <ChevronUp className="h-4 w-4" />
                    <span className="font-bold">{item.voteCount}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1a1a1a] dark:text-white truncate">
                    {item.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`text-xs ${categoryBadgeConfig[item.category]}`}>
                      {categoryLabels[item.category]}
                    </Badge>
                    <Badge className={`text-xs ${statusBadgeConfig[item.status].className}`}>
                      {statusBadgeConfig[item.status].label}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </FluentCard>
      )}

      {/* Data Table */}
      <FluentCard className="overflow-hidden p-5">
        <DataTable
          columns={columns}
          data={requests}
          totalCount={totalCount}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={setSort}
          filters={filters}
          onFilterChange={setFilters}
          searchValue={search}
          onSearchChange={setSearch}
          rowKey="id"
          searchPlaceholder="Search by title, description..."
          emptyMessage="No feature requests found"
        />
      </FluentCard>

      {/* View Details Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Feature Request Details</DialogTitle>
            <DialogDescription>
              Full details for this feature request
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-[#1a1a1a] dark:text-white">
                  {selectedRequest.title}
                </h3>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={categoryBadgeConfig[selectedRequest.category]}>
                    {categoryLabels[selectedRequest.category]}
                  </Badge>
                  <Badge className={statusBadgeConfig[selectedRequest.status].className}>
                    {statusBadgeConfig[selectedRequest.status].label}
                  </Badge>
                  <div className="flex items-center gap-1 text-violet-600 dark:text-violet-400">
                    <ChevronUp className="h-4 w-4" />
                    <span className="font-semibold">{selectedRequest.voteCount} votes</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[#888] text-xs uppercase tracking-wide">Description</Label>
                <p className="mt-2 text-[#616161] dark:text-[#adadad] whitespace-pre-wrap">
                  {selectedRequest.description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Submitted By</Label>
                  <p className="font-medium mt-1">
                    {selectedRequest.submittedByName || selectedRequest.submittedByEmail || 'Anonymous'}
                  </p>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Submitted At</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedRequest.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {selectedRequest.adminResponse && (
                <div className="p-4 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800/30">
                  <div className="flex items-center gap-2 mb-2 text-sm font-medium text-violet-700 dark:text-violet-300">
                    <MessageSquare className="h-4 w-4" />
                    Admin Response
                  </div>
                  <p className="text-violet-900 dark:text-violet-100 whitespace-pre-wrap">
                    {selectedRequest.adminResponse}
                  </p>
                  {selectedRequest.respondedAt && (
                    <p className="text-xs text-violet-600 dark:text-violet-400 mt-2">
                      Responded on {new Date(selectedRequest.respondedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-[#e5e5e5] dark:border-[#3d3d3d]">
                <Button variant="outline" onClick={() => setViewModalOpen(false)}>
                  Close
                </Button>
                <Button onClick={() => {
                  setViewModalOpen(false)
                  openStatusModal(selectedRequest)
                }}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Update Status
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Feature Request Status</DialogTitle>
            <DialogDescription>
              Change the status and add an optional response for "{selectedRequest?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as FeatureRequestStatus)}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(statusBadgeConfig).map(([value, { label }]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="adminResponse">Admin Response (optional)</Label>
              <Textarea
                id="adminResponse"
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Add a public response explaining the status change..."
                className="mt-2"
                rows={4}
              />
              <p className="text-xs text-[#888] mt-1">
                This response will be visible to the user who submitted the request.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setStatusModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateStatus} disabled={actionLoading}>
              {actionLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Status'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Feature Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{selectedRequest?.title}"?
              This action cannot be undone and will remove all votes associated with this request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
